import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, useAuth } from '@onvice/portal-shell-api';

type Mode = 'light' | 'dark';

interface TenantTheme {
  name?: string;
  appName?: string;
  /** Lucide-Icon-Name (z.B. "Zap", "Wrench", "Car"). Default: "Shield". */
  icon?: string;
  primary?: string;
  primaryHover?: string;
  primaryForeground?: string;
  accent?: string;
  accentHover?: string;
  accentForeground?: string;
  fontFamily?: string;
  radiusCard?: string;
  radiusButton?: string;
}

interface ThemeContextValue {
  mode: Mode;
  setMode: (m: Mode) => void;
  toggleMode: () => void;
  tenant: TenantTheme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const LS_KEY = 'onvice.theme.mode';

function applyMode(mode: Mode) {
  document.documentElement.setAttribute('data-mode', mode);
}

function applyTenantTheme(theme: TenantTheme) {
  const root = document.documentElement;
  if (theme.primary) root.style.setProperty('--color-primary', theme.primary);
  if (theme.primaryHover) root.style.setProperty('--color-primary-hover', theme.primaryHover);
  if (theme.primaryForeground) root.style.setProperty('--color-primary-foreground', theme.primaryForeground);
  if (theme.accent) root.style.setProperty('--color-accent', theme.accent);
  if (theme.accentHover) root.style.setProperty('--color-accent-hover', theme.accentHover);
  if (theme.accentForeground) root.style.setProperty('--color-accent-foreground', theme.accentForeground);
  if (theme.fontFamily) root.style.setProperty('--font-family', theme.fontFamily);
  if (theme.radiusCard) root.style.setProperty('--radius-card', theme.radiusCard);
  if (theme.radiusButton) root.style.setProperty('--radius-button', theme.radiusButton);
  if (theme.fontFamily) document.body.style.fontFamily = theme.fontFamily;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initial: LocalStorage > Default 'dark' (sofortige Anwendung, kein Flash)
  const initialMode = ((): Mode => {
    if (typeof window === 'undefined') return 'dark';
    const ls = localStorage.getItem(LS_KEY);
    return ls === 'light' || ls === 'dark' ? ls : 'dark';
  })();

  const [mode, setModeState] = useState<Mode>(initialMode);
  const [tenant, setTenant] = useState<TenantTheme>({});
  // Martin-Fund 2026-09-17: ThemeProvider muss INNERHALB des AuthProviders
  // stehen (App.tsx), damit der DB-Wert bei Login/Userwechsel nachgeladen wird —
  // vorher lief die Abfrage genau einmal beim App-Start, also vor dem Login.
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => { applyMode(mode); }, [mode]);

  // Tenant-Theme laden (einmal beim Mount)
  useEffect(() => {
    api.get<{ success: boolean; data: TenantTheme }>('/theme')
      .then((res) => {
        if (res.data) {
          setTenant(res.data);
          applyTenantTheme(res.data);
        }
      })
      .catch(() => { /* kein Theme = Default-Werte */ });
  }, []);

  // Mode aus DB laden — nur bei angemeldetem User, und erneut bei jedem
  // Userwechsel. Ohne Session liefert der Server `mode: null` → lokaler Stand gilt.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api.get<{ success: boolean; data: { mode: Mode | null } }>('/auth/theme-mode')
      .then((res) => {
        if (cancelled) return;
        const dbMode = res.data?.mode;
        if (dbMode === 'light' || dbMode === 'dark') {
          setModeState(dbMode);
          localStorage.setItem(LS_KEY, dbMode);
        }
      })
      .catch(() => { /* Netzfehler — LocalStorage gilt */ });
    return () => { cancelled = true; };
  }, [userId]);

  const setMode = (m: Mode) => {
    setModeState(m);
    localStorage.setItem(LS_KEY, m);
    // Speichern darf nie still scheitern (feedback_keine_stillen_fehler) — der
    // lokale Stand bleibt, aber der Fehler landet in der Konsole.
    api.put('/auth/theme-mode', { mode: m }).catch((err: unknown) => {
      console.warn('[Theme] Modus konnte nicht am Server gespeichert werden:', (err as Error)?.message);
    });
  };

  const toggleMode = () => setMode(mode === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ mode, setMode, toggleMode, tenant }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden');
  return ctx;
}
